import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { IconClose, IconLogout, IconMenu, ModuleIcon } from './icons';
import { ThemeToggle, type ThemeApi } from './theme';

/**
 * Оболочка приложения.
 *
 * До этого меню было жёсткой колонкой `w-64` без единого брейкпоинта: на телефоне
 * оно занимало две трети экрана, и добраться до содержимого было нельзя вовсе.
 * Здесь одна разметка на все размеры: от `lg` — постоянная колонка, ниже — та же
 * колонка выезжает поверх содержимого и закрывается по Esc, по клику вне и по
 * выбору раздела. Второй разметки для мобильного нет намеренно: два дерева меню
 * разъезжаются при первом же добавлении раздела.
 */
export interface NavItem {
  readonly key: string;
  readonly label: string;
}

export interface AppFrameProps {
  readonly brand: string;
  readonly userName: string;
  readonly userRoles: string;
  readonly nav: readonly NavItem[];
  readonly activeKey: string | undefined;
  readonly onNavigate: (key: string) => void;
  readonly navLabel: string;
  readonly navNote?: ReactNode;
  readonly siteSelect: ReactNode;
  readonly theme: ThemeApi;
  readonly onLogout: () => void;
  readonly logoutLabel: string;
  readonly children: ReactNode;
}

export function AppFrame({
  brand,
  userName,
  userRoles,
  nav,
  activeKey,
  onNavigate,
  navLabel,
  navNote,
  siteSelect,
  theme,
  onLogout,
  logoutLabel,
  children,
}: AppFrameProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);

  // Пока меню поверх содержимого, страница под ним не должна прокручиваться:
  // иначе палец «промахивается» и уводит фон, а меню остаётся на месте.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);

    drawer.current?.querySelector<HTMLElement>('button, a')?.focus();

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  const close = () => {
    setDrawerOpen(false);
    menuButton.current?.focus();
  };

  const navList = (
    <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label={navLabel}>
      <p className="px-2 pb-1.5 text-[11px] font-medium tracking-wider text-ink-muted uppercase">
        {navLabel}
      </p>
      <ul className="flex flex-col gap-0.5">
        {nav.map((item) => {
          const current = item.key === activeKey;
          return (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => {
                  onNavigate(item.key);
                  setDrawerOpen(false);
                }}
                aria-current={current ? 'page' : undefined}
                className={`flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-left text-sm transition ${
                  current
                    ? 'bg-brand-50 font-medium text-brand-700'
                    : 'text-ink-muted hover:bg-sunken hover:text-ink'
                }`}
              >
                <ModuleIcon
                  module={item.key}
                  size={17}
                  className={current ? 'text-brand-600' : 'text-ink-faint'}
                />
                <span className="min-w-0 truncate">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {navNote ? <div className="px-2.5 pt-3 text-xs text-ink-faint">{navNote}</div> : null}
    </nav>
  );

  const identity = (
    <div className="border-t border-line px-4 py-3">
      <p className="truncate text-sm font-medium text-ink">{userName}</p>
      <p className="truncate text-xs text-ink-muted">{userRoles}</p>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:px-4">
        <button
          ref={menuButton}
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label={navLabel}
          aria-expanded={drawerOpen}
          className="flex h-9 w-9 items-center justify-center rounded-control text-ink-muted transition hover:bg-sunken hover:text-ink lg:hidden"
        >
          <IconMenu size={20} />
        </button>

        <span className="shrink-0 truncate text-sm font-semibold tracking-tight text-ink">
          {brand}
        </span>

        <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-2">
          <div className="min-w-0 max-w-[11rem] sm:max-w-[18rem]">{siteSelect}</div>
          <ThemeToggle theme={theme} />
          <button
            type="button"
            onClick={onLogout}
            title={logoutLabel}
            aria-label={logoutLabel}
            className="flex h-9 items-center gap-2 rounded-control px-2 text-sm text-ink-muted transition hover:bg-sunken hover:text-ink"
          >
            <IconLogout size={18} />
            <span className="hidden xl:inline">{logoutLabel}</span>
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface lg:flex">
          {navList}
          {identity}
        </aside>

        {drawerOpen ? (
          <div
            className="fixed inset-0 z-40 bg-scrim lg:hidden"
            style={{ animation: 'moika-overlay-in 120ms ease-out' }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div
              ref={drawer}
              role="dialog"
              aria-modal="true"
              aria-label={navLabel}
              className="flex h-full w-[min(17rem,86vw)] flex-col border-r border-line bg-surface shadow-sheet"
              style={{ animation: 'moika-sheet-left 160ms ease-out' }}
            >
              <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-4">
                <span className="truncate text-sm font-semibold text-ink">{brand}</span>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Закрыть меню"
                  className="flex h-9 w-9 items-center justify-center rounded-control text-ink-muted transition hover:bg-sunken hover:text-ink"
                >
                  <IconClose size={18} />
                </button>
              </div>
              {navList}
              {identity}
            </div>
          </div>
        ) : null}

        <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
